import React, { useState, useEffect } from 'react';
import { X, Save, Server, Globe, Key, FileText, Activity } from 'lucide-react';

const ServerManagementModal = ({ isOpen, onClose, server, onSave, credentials = [] }) => {
    const [formData, setFormData] = useState({
        name: '',
        ip_address: '',
        credential_id: '',
        description: '',
        status: 'active'
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (server) {
                setFormData({
                    name: server.name || '',
                    ip_address: server.ip_address || '',
                    credential_id: server.credential_id || '',
                    description: server.description || '',
                    status: server.status || 'active'
                });
            } else {
                setFormData({
                    name: '',
                    ip_address: '',
                    credential_id: '',
                    description: '',
                    status: 'active'
                });
            }
            setErrors({});
        }
    }, [isOpen, server]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Sunucu adı zorunludur';
        if (!formData.ip_address?.trim()) newErrors.ip_address = 'IP adresi zorunludur';
        // basic IP validation could be added here
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSave({ ...formData, id: server?.id });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#252525]">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Server className="w-5 h-5 text-blue-500" />
                        {server ? 'Sunucuyu Düzenle' : 'Yeni Sunucu Ekle'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">
                                    <span className="flex items-center gap-1"><Server className="w-3.5 h-3.5 inline" /> Sunucu Adı <span className="text-red-500">*</span></span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                    placeholder="Örn: Prod-DB-01"
                                />
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">
                                    <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5 inline" /> IP Adresi <span className="text-red-500">*</span></span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.ip_address}
                                    onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors font-mono"
                                    placeholder="192.168.1.10"
                                />
                                {errors.ip_address && <p className="text-xs text-red-500 mt-1">{errors.ip_address}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">
                                    <span className="flex items-center gap-1"><Activity className="w-3.5 h-3.5 inline" /> Durum</span>
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                >
                                    <option value="active">Aktif</option>
                                    <option value="inactive">Pasif</option>
                                    <option value="maintenance">Bakım Modu</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">
                                    <span className="flex items-center gap-1"><Key className="w-3.5 h-3.5 inline" /> SSH Kimliği</span>
                                </label>
                                <select
                                    value={formData.credential_id}
                                    onChange={(e) => setFormData({ ...formData, credential_id: e.target.value })}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                >
                                    <option value="">Seçiniz...</option>
                                    {credentials.map(cred => (
                                        <option key={cred.id} value={cred.id}>{cred.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Sunucuya bağlanmak için kullanılacak kimlik.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">
                                    <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 inline" /> Açıklama</span>
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={4}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors resize-none"
                                    placeholder="Sunucu hakkında notlar..."
                                />
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 bg-[#252525] flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                        İptal
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServerManagementModal;
